import { ref as vueRef, computed, watch, getCurrentInstance, onUnmounted } from "vue";
import { getDatabase, ref, onValue } from "firebase/database";
import { authStore } from "@/store/auth";
import { addLearningLanguage } from "@/scripts/learningLanguageApi.js";
import { mapLanguageNameToLocale } from "@/locales/index.js";
import i18n from "@/locales";

// Form state for adding a learning language, shared by the desktop modal and
// the mobile stepped flow. Same canonical keys / locale strings as the
// onboarding wizard.
export function useAddLanguageForm() {
  const pickedLanguage = vueRef(null);
  const pickedReason = vueRef(null);
  const pickedProficiency = vueRef(null);
  const isSubmitting = vueRef(false);
  const errorKey = vueRef(null);
  const nativeLanguage = vueRef(null);
  const heldCodes = vueRef([]);
  const activeLanguageCode = vueRef(null);

  // The listener follows authStore.userId: at mount the uid may not have
  // resolved yet, and it changes again on sign-out/sign-in.
  let off = null;
  const stopUserWatch = watch(
    () => authStore.userId,
    (userId) => {
      if (off) {
        off();
        off = null;
      }
      nativeLanguage.value = null;
      heldCodes.value = [];
      activeLanguageCode.value = null;
      if (!userId) return;
      off = onValue(
        ref(getDatabase(), `users/${userId}`),
        (snapshot) => {
          const data = snapshot.val() || {};
          nativeLanguage.value = data.nativeLanguage || null;
          heldCodes.value = Object.keys(data.languagePreferences || {});
          activeLanguageCode.value = data.selectedLanguage || null;
        },
        (error) => {
          console.error("AddLanguageForm user read failed:", error);
        }
      );
    },
    { immediate: true }
  );
  if (getCurrentInstance()) {
    onUnmounted(() => {
      if (off) off();
      stopUserWatch();
    });
  }

  const reasons = computed(() =>
    ["career", "travel", "fun", "relationships", "culture", "school", "other"].map(
      (key) => ({ key, label: i18n.global.t(`onboarding.newLanguage.reasons.${key}`) })
    )
  );
  const proficiencyLevels = computed(() =>
    ["beginner", "elementary", "intermediate", "upperIntermediate", "advanced"].map(
      (key) => ({ key, label: i18n.global.t(`onboarding.newLanguage.proficiency.${key}`) })
    )
  );
  const nativeLanguageCode = computed(() =>
    nativeLanguage.value ? mapLanguageNameToLocale(nativeLanguage.value) : null
  );
  const isValid = computed(
    () => !!(pickedLanguage.value && pickedReason.value && pickedProficiency.value)
  );

  const reset = () => {
    pickedLanguage.value = null;
    pickedReason.value = null;
    pickedProficiency.value = null;
    errorKey.value = null;
  };

  // Resolves true on success so the caller owns the success UX (toast,
  // haptic, close). selectedLanguage now points at the new language, so
  // every view re-renders without further plumbing.
  const submit = async () => {
    if (!isValid.value || isSubmitting.value) return false;
    isSubmitting.value = true;
    errorKey.value = null;
    try {
      await addLearningLanguage({
        lang: pickedLanguage.value,
        proficiency: pickedProficiency.value,
        reasonForLearning: pickedReason.value,
      });
      return true;
    } catch (error) {
      errorKey.value =
        error?.code === "alreadyAdded" ? "addLanguage.alreadyAdded" : "addLanguage.failed";
      return false;
    } finally {
      isSubmitting.value = false;
    }
  };

  return {
    pickedLanguage,
    pickedReason,
    pickedProficiency,
    isSubmitting,
    errorKey,
    reasons,
    proficiencyLevels,
    nativeLanguageCode,
    heldCodes,
    activeLanguageCode,
    isValid,
    reset,
    submit,
  };
}
